import React from "react";
import { ShoppingBag, Activity, Cpu, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const verticals = [{
  id: "api-architecture",
  title: "API Architecture",
  subtitle: "SYSTEM INTEGRATION",
  description: "Salesforce SI, ISV & OEM integration, RESTful systems, and scalable enterprise-grade computing ecosystems built for speed.",
  icon: Cpu,
  colorClass: "text-primary",
  glowClass: "shadow-[0_0_30px_-5px_var(--color-primary)]",
  gradient: "from-primary/20 to-transparent",
  link: "/SystemsPage"
}, {
  id: "government-systems",
  title: "Solution Engineering",
  subtitle: "APPLIED INTELLIGENCE",
  description: "ADVANCED SOLUTION ENGINEERING, TECHNICAL BLUEPRINTING, AND COMPLEX SYSTEMS ARCHITECTURE DESIGNED TO NAVIGATE THE INTERSECTION OF ENTERPRISE SCALE AND DIGITAL INFRASTRUCTURE.",
  icon: Activity,
  colorClass: "text-primary",
  glowClass: "shadow-[0_0_30px_-5px_var(--color-primary)]",
  gradient: "from-primary/20 to-transparent",
  link: "/IntelligencePage"
}, {
  id: "brand-strategy",
  title: "Business Development",
  subtitle: "PRODUCT VISION",
  description: "Market-leading positioning, sovereign brand identity, and visionary storytelling for next-generation AI and crypto platforms.",
  icon: ShoppingBag,
  colorClass: "text-primary",
  glowClass: "shadow-[0_0_30px_-5px_var(--color-primary)]",
  gradient: "from-primary/20 to-transparent",
  link: "/InsightsPage"
}];
export default function IndustryVerticals() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="3010" id="strategic-capabilities" ref={ref} className="relative w-full py-24 overflow-hidden bg-background dark">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px]" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-3xl md:text-5xl font-default font-bold tracking-tighter mb-4 text-foreground uppercase">
            Strategic Capabilities
          </h2>
          <p className="text-lg font-default text-muted-foreground">Sophisticated engineering and visionary strategy to power the future of modern digital infrastructure.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {verticals.map((item, i) => <div key={item.id} data-index={i} className={`group transition-all duration-700 delay-[${i * 150}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              <Link to={item.link} className="block h-full">
                <Card className="h-full relative overflow-hidden border border-white/10 bg-card/40 backdrop-blur-xl transition-all duration-500 hover:scale-[1.02] hover:bg-card/60 hover:border-primary/50 hover:shadow-2xl">
                  {}
                  <div className={cn("absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-500 group-hover:opacity-100", item.gradient)} />

                  <CardHeader className="relative z-10 pb-2">
                    <div className="mb-6 flex items-center justify-between">
                      <div className={cn("p-3 rounded-sm bg-background/50 border border-white/5 transition-all duration-500 group-hover:scale-110", "group-hover:shadow-[0_0_20px_rgba(0,225,255,0.3)]")}>
                        <item.icon className={cn("size-10", item.colorClass)} strokeWidth={1} />
                      </div>
                      <span className="font-mono text-[10px] tracking-widest uppercase text-primary/80 bg-primary/5 px-2 py-1 rounded-sm border border-primary/20">
                        {item.subtitle}
                      </span>
                    </div>
                    <CardTitle className="text-2xl font-default font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="relative z-10">
                    <p className="text-muted-foreground font-mono text-xs leading-relaxed uppercase tracking-tight">
                      {item.description}
                    </p>
                  </CardContent>

                  <CardFooter className="relative z-10 pt-4 mt-auto">
                    <div className="flex items-center text-[11px] font-mono tracking-widest uppercase text-foreground group-hover:text-primary transition-colors">
                      Explore Capability
                      <ArrowRight className="ml-2 size-3 transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </CardFooter>
                </Card>
              </Link>
            </div>)}
        </div>
      </div>
    </section>;
}